import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Upload,
  FileText,
  CheckCircle,
  AlertCircle,
  User,
  Mail,
  Phone,
  MapPin,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { firebaseApp, functions } from "../../firebase";
import { getFirestore, doc, setDoc, getDoc, Timestamp } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import * as pdfjsLib from "pdfjs-dist";
import mammoth from "mammoth";
import Tooltip from "../components/JobSeekerTooltip";
import gamificationService from "../../services/gamificationService";
import toast from "react-hot-toast";

// Set up PDF.js worker - use local worker to avoid CORS issues
if (typeof window !== "undefined") {
  pdfjsLib.GlobalWorkerOptions.workerSrc = `${window.location.origin}/pdf.worker.min.mjs`;
  console.log(
    "PDF.js worker configured:",
    pdfjsLib.GlobalWorkerOptions.workerSrc
  );
}

const ResumeUpload = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const db = getFirestore(firebaseApp);
  const [uploadStep, setUploadStep] = useState("upload");
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [resumeUrl, setResumeUrl] = useState(null);
  const [savingStatus, setSavingStatus] = useState("");
  const [classification, setClassification] = useState(null);
  const [isClassifying, setIsClassifying] = useState(false);

  // Extract text from PDF/Doc files
  const extractTextFromFile = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const arrayBuffer = e.target?.result;

          if (file.type === "application/pdf") {
            // Extract text from PDF using PDF.js
            try {
              console.log("Starting PDF text extraction...");
              const uint8Array = new Uint8Array(arrayBuffer);
              console.log("Created Uint8Array, size:", uint8Array.length);

              const pdf = await pdfjsLib.getDocument({ data: uint8Array })
                .promise;
              console.log("PDF loaded successfully, pages:", pdf.numPages);

              let fullText = "";

              // Extract text from each page
              for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
                console.log(`Processing page ${pageNum}...`);
                const page = await pdf.getPage(pageNum);
                const textContent = await page.getTextContent();
                const pageText = textContent.items
                  .map((item) => item.str)
                  .join(" ");
                fullText += pageText + "\n";
                console.log(
                  `Page ${pageNum} extracted ${pageText.length} characters`
                );
              }

              console.log("Total extracted text length:", fullText.length);

              if (fullText.trim().length > 0) {
                console.log("PDF text extraction successful!");
                resolve(fullText.trim());
              } else {
                throw new Error("No text found in PDF");
              }
            } catch (pdfError) {
              console.error("PDF parsing error:", pdfError);
              const errorMessage =
                pdfError instanceof Error
                  ? pdfError.message
                  : "Unknown error occurred";
              console.error("Error details:", {
                name: pdfError instanceof Error ? pdfError.name : "Unknown",
                message: errorMessage,
                stack: pdfError instanceof Error ? pdfError.stack : undefined,
              });
              // Reject instead of using fallback data so the user knows parsing failed
              reject(new Error(`Failed to parse PDF: ${errorMessage}`));
            }
          } else if (file.name.endsWith(".docx")) {
            // Extract text from DOCX using mammoth
            try {
              const result = await mammoth.extractRawText({ arrayBuffer });
              resolve(result.value);
            } catch (docError) {
              console.error("DOCX parsing error:", docError);
              const errorMessage =
                docError instanceof Error
                  ? docError.message
                  : "Unknown error occurred";
              reject(new Error(`Failed to parse DOCX: ${errorMessage}`));
            }
          } else {
            // For DOC files or unsupported formats
            reject(
              new Error(
                `Unsupported file format: ${file.type || "unknown"
                }. Only PDF and DOCX files are supported for text extraction.`
              )
            );
          }
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsArrayBuffer(file);
    });
  };

  // Parse resume text using OpenAI GPT-5.2 (via HTTP function with CORS)
  const parseResumeWithAI = async (resumeText) => {
    try {
      // Get Firebase ID token for authentication using Firebase Auth directly
      const auth = getAuth(firebaseApp);
      const currentUser = auth.currentUser;

      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const idToken = await currentUser.getIdToken();

      // Determine the API URL based on environment
      // const isDev = process.env.NODE_ENV === 'development';
      const apiUrl = 'https://us-central1-vetriaai-mvp.cloudfunctions.net/parseResume';

      console.log(`Calling parseResume API at: ${apiUrl}`);

      const response = await fetch(
        apiUrl,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`,
          },
          body: JSON.stringify({ resumeText }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || `HTTP error! status: ${response.status}`);
      }

      if (result.success && result.data) {
        return result.data;
      }

      throw new Error('Invalid response from resume parser');
    } catch (error) {
      console.error('Error calling parseResume function:', error);
      throw new Error(error.message || 'Failed to parse resume');
    }
  };

  // Classify job seeker  or non-technical
  const classifyJobSeekerProfile = async (resumeData, rawResumeText) => {
    try {
      setIsClassifying(true);

      const response = await fetch(
        'https://us-central1-vetriaai-mvp.cloudfunctions.net/classifyJobSeeker',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ resumeData, rawResumeText }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || `HTTP error! status: ${response.status}`);
      }

      if (result.success && result.classification) {
        return result.classification;
      }

      throw new Error('Invalid response from classification service');
    } catch (error) {
      console.error('Error classifying job seeker:', error);
      // Don't throw - we'll handle missing classification gracefully
      return null;
    } finally {
      setIsClassifying(false);
    }
  };

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const onDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (
      droppedFile &&
      (droppedFile.type === "application/pdf" ||
        droppedFile.name.endsWith(".doc") ||
        droppedFile.name.endsWith(".docx"))
    ) {
      handleFileUpload(droppedFile);
    }
  }, []);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      handleFileUpload(selectedFile);
    }
  };

  // Validate file before processing
  const validateFile = (file) => {
    // Check file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      return "File size must be less than 10MB";
    }

    // Check file type
    const validTypes = ["application/pdf"];
    const validExtensions = [".pdf", ".doc", ".docx"];

    const isValidType =
      validTypes.includes(file.type) ||
      validExtensions.some((ext) => file.name.toLowerCase().endsWith(ext));

    if (!isValidType) {
      return "Only PDF, DOC, and DOCX files are supported";
    }

    return null;
  };

  const handleFileUpload = async (uploadedFile) => {
    // Validate file first
    const validationError = validateFile(uploadedFile);
    if (validationError) {
      alert(validationError);
      return;
    }

    setFile(uploadedFile);
    setIsProcessing(true);

    try {
      // Skip Firebase Storage for now - focus on AI processing
      console.log("Processing resume file:", uploadedFile.name);
      // We'll process without storing the file and focus on text extraction + AI

      // Extract text from the file
      let resumeText;
      try {
        resumeText = await extractTextFromFile(uploadedFile);
        if (!resumeText || resumeText.trim().length < 50) {
          throw new Error("Could not extract meaningful text from the file");
        }
      } catch (extractionError) {
        console.error("Text extraction failed:", extractionError);
        const errorMessage =
          extractionError instanceof Error
            ? extractionError.message
            : "Unknown error occurred";
        throw new Error(
          `Failed to extract text from the resume file: ${errorMessage}`
        );
      }

      // Parse the text using OpenAI GPT-5.2 via HTTP function with CORS
      let parsedData;
      try {
        parsedData = await parseResumeWithAI(resumeText);

        // Validate parsed data structure
        if (!parsedData || typeof parsedData !== "object") {
          throw new Error("Invalid data structure returned from AI parsing");
        }

        // Ensure required fields are present
        if (!parsedData.personalInfo && !parsedData.name) {
          throw new Error("Could not extract personal information from resume");
        }

        // Store the raw resume text in the parsed data
        parsedData.rawResumeText = resumeText;
      } catch (aiError) {
        console.error("AI parsing failed:", aiError);
        // Don't throw here, will use fallback data
        parsedData = null;
      }

      // Use parsed data or fallback
      if (parsedData) {
        setExtractedData(parsedData);

        // Classify the job seeker in the background
        classifyJobSeekerProfile(parsedData, resumeText).then((classificationResult) => {
          if (classificationResult) {
            setClassification(classificationResult);
          }
        });
      } else {
        // Use fallback data with a warning
        const fallbackData = {
          personalInfo: {
            name: "[Please Edit]",
            email: "[Please Edit]",
            phone: "[Please Edit]",
            location: "[Please Edit]",
          },
          professionalInfo: {
            summary:
              "[Please edit this professional summary based on your resume]",
            experience: "[Please Edit]",
            skills: ["[Please", "edit", "these", "skills"],
            education: "[Please Edit]",
          },
          workHistory: [
            {
              title: "[Job Title]",
              company: "[Company Name]",
              duration: "[Start Date] - [End Date]",
              description:
                "[Please describe your responsibilities and achievements]",
            },
          ],
          additionalInfo: {
            certifications: [],
            languages: [],
            projects: [],
          },
          rawResumeText: resumeText,
        };

        setExtractedData(fallbackData);
        // Show warning about fallback data
        alert(
          "AI parsing encountered issues. Please review and edit all fields in the preview."
        );
      }

      setUploadStep("preview");
    } catch (error) {
      console.error("Resume processing failed:", error);

      // Show user-friendly error message
      let errorMessage = "Failed to process resume. ";
      if (error instanceof Error) {
        errorMessage += error.message;
      } else {
        errorMessage +=
          "Please try again or contact support if the issue persists.";
      }

      alert(errorMessage);

      // Reset state
      setFile(null);
      setResumeUrl(null);
      setExtractedData(null);
    } finally {
      setIsProcessing(false);
    }
  };

  // Normalize resume data to match expected profile schema
  const normalizeResumeData = (data) => {
    if (!data) return {};

    // If already has personalInfo structure, return as is
    if (data.personalInfo && data.professionalInfo) {
      return data;
    }

    // Otherwise, transform flat structure to nested structure
    return {
      personalInfo: {
        name: data.name || data.fullName || "",
        email: data.email || "",
        phone: data.phone || "",
        location: data.location || data.city || "",
      },
      professionalInfo: {
        jobTitle: data.jobTitle || data.title || data.currentPosition || "",
        summary: data.summary || data.bio || data.professionalSummary || "",
        experience: data.yearsOfExperience || data.experience || "Not specified",
        skills: Array.isArray(data.skills) ? data.skills : [],
        education: data.education || "Not specified",
      },
      workHistory: Array.isArray(data.workHistory) ? data.workHistory : [],
      projects: Array.isArray(data.projects) ? data.projects : [],
      education: Array.isArray(data.education) ? data.education : (data.education || []),
      additionalInfo: {
        certifications: Array.isArray(data.certifications) ? data.certifications : (data.additionalInfo?.certifications || []),
        languages: Array.isArray(data.languages) ? data.languages : (data.additionalInfo?.languages || []),
      },
      socialMedia: data.socialMedia || {},
      rawResumeText: data.rawResumeText || "",
    };
  };

  const handleConfirm = async () => {
    if (!user || !extractedData) {
      alert("Missing user information or resume data. Please try again.");
      return;
    }

    try {
      // Validate required fields before saving
      const personalInfo = extractedData.personalInfo || {};
      const userName = personalInfo.name || extractedData.name || "";

      if (!userName || userName === "[Please Edit]" || userName.trim() === "") {
        alert("Please enter your full name before confirming.");
        return;
      }

      if (!personalInfo.email || personalInfo.email === "[Please Edit]") {
        alert("Please enter your email address before confirming.");
        return;
      }

      // Show saving indicator IMMEDIATELY
      setSavingStatus("Saving your profile to database...");
      setIsProcessing(true);

      // Small delay to ensure modal renders
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Check if this is the first resume upload (for points)
      const userProfileRef = doc(db, "userProfiles", user.id);
      const existingProfile = await getDoc(userProfileRef);
      const isFirstUpload = !existingProfile.exists() || !existingProfile.data()?.resumeUploaded;

      // Store the raw resume text in separate collection for easy access
      await setDoc(
        doc(db, "resumeData", user.id),
        {
          userId: user.id,
          resumeText: extractedData.rawResumeText || "",
          fileName: file?.name || "unknown",
          uploadedAt: Timestamp.now(),
        },
        { merge: false } // Always overwrite with new resume
      );

      // Save the parsed resume data to Firebase and reset verification status
      const normalizedResumeData = normalizeResumeData(extractedData);
      console.log("Normalized resume data structure:", normalizedResumeData);

      await setDoc(
        userProfileRef,
        {
          userId: user.id,
          resumeData: normalizedResumeData,
          resumeUploaded: true,
          resumeUrl: resumeUrl,
          fileName: file?.name || "unknown",
          fileSize: file?.size || 0,
          fileType: file?.type || "unknown",
          lastUpdated: new Date(),
          processingVersion: "v2.0", // Track which version processed this resume
          // Reset verification status when new resume is uploaded
          skillsVerified: false,
          verificationScore: null,
          verificationDate: null,
          // Reset course completion status
          courseCompleted: false,
          courseTestPassed: false,
          // Classification data (if available)
          ...(classification && {
            isTechnical: classification.isTechnical,
            profileType: classification.profileType,
            primaryDomain: classification.primaryDomain,
            classificationConfidence: classification.confidence,
          }),
        },
        { merge: true }
      );

      // Also update the jobseekers collection with classification
      if (classification) {
        await setDoc(
          doc(db, "jobseekers", user.id),
          {
            isTechnical: classification.isTechnical,
            profileType: classification.profileType,
            primaryDomain: classification.primaryDomain,
            lastClassified: Timestamp.now(),
          },
          { merge: true }
        );
      }

      console.log("Resume data saved to Firebase successfully");

      // Award 5 points for first resume upload (combined with login)
      if (isFirstUpload) {
        try {
          await gamificationService.addXP(user.id, 5);
          await gamificationService.addPoints(user.id, 5);
          toast.success("🎉 +5 points for uploading your resume!");
        } catch (error) {
          console.error("Failed to award points:", error);
        }
      } else {
        toast("Resume updated! Your verification status h reset.", {
          icon: "ℹ️",
        });
      }

      setSavingStatus("Profile saved! Generating skill validation test...");

      // Generate and navigate to skills validation test
      await generateSkillsValidationTest();
    } catch (error) {
      console.error("Failed to save resume data:", error);

      // Show user-friendly error message
      alert(
        "Failed to save your profile data. Please check your internet connection and try again."
      );

      // Don't proceed to complete step if save fails
      setSavingStatus("");
    } finally {
      setIsProcessing(false);
    }
  };

  // After resume upload: take user to the scheduler (Dashboard) instead of auto-starting the test
  const generateSkillsValidationTest = async () => {
    try {
      setSavingStatus("Preparing your skills validation scheduling...");

      if (!extractedData || !user) {
        throw new Error("Missing resume data or user information");
      }

      // We no longer auto-generate the test here.
      // The Dashboard will render the SkillsValidationScheduler, allowing the user to choose Now/Today/Tomorrow.
      // Just redirect to the dashboard.
      await new Promise((resolve) => setTimeout(resolve, 600));
      navigate("/job-seeker/profile", { replace: true });
      return;
    } catch (error) {
      console.error("Failed to generate skills validation test:", error);
      setSavingStatus("");
      setIsProcessing(false);

      // More user-friendly error with option to retry
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      alert(
        `Failed to generate test: ${errorMessage}\n\nYour profile h saved. You can take the skills validation test from your profile page.`
      );

      // Navigate to profile instead
      navigate("/job-seeker/profile", { replace: true });
    } finally {
      setSavingStatus("");
      setIsProcessing(false);
    }
  };

  // Helper functions
  const extractYearsFromExperience = (experience) => {
    // If experience is an array, convert to string first
    if (Array.isArray(experience)) {
      // Try to calculate years from work history
      if (experience.length > 0) {
        return `${experience.length}+ years`;
      }
      return "Not specified";
    }

    // If experience is a string, try to extract years
    if (typeof experience === 'string') {
      const match = experience.match(/(\d+)[\+\-]?\s*(year|yr)/i);
      return match ? `${match[1]} years` : "Not specified";
    }

    return "Not specified";
  };

  const determineDomain = (skills, workHistory) => {
    const techKeywords = [
      "javascript",
      "python",
      "react",
      "node",
      "java",
      "programming",
      "software",
      "developer",
    ];
    const marketingKeywords = [
      "marketing",
      "seo",
      "social media",
      "content",
      "advertising",
    ];
    const designKeywords = [
      "design",
      "ui",
      "ux",
      "photoshop",
      "figma",
      "creative",
    ];
    const dataKeywords = [
      "data",
      "analytics",
      "sql",
      "tableau",
      "analysis",
      "science",
    ];

    const allText = [
      ...skills,
      ...(Array.isArray(workHistory) ? workHistory.map((w) => w.title + " " + w.description) : []),
    ]
      .join(" ")
      .toLowerCase();

    if (techKeywords.some((keyword) => allText.includes(keyword)))
      return "Technology";
    if (marketingKeywords.some((keyword) => allText.includes(keyword)))
      return "Marketing";
    if (designKeywords.some((keyword) => allText.includes(keyword)))
      return "Design";
    if (dataKeywords.some((keyword) => allText.includes(keyword)))
      return "Data & Analytics";

    return "General";
  };

  const handleReupload = () => {
    setFile(null);
    setExtractedData(null);
    setUploadStep("upload");
  };

  const handleViewProfile = () => {
    navigate("/job-seeker/profile");
  };

  if (uploadStep === "complete") {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="text-center py-12">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Resume Uploaded Successfully!
          </h1>
          <p className="text-lg text-gray-600 mb-8">
            Your resume  processed and your profile updated.
          </p>

          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 max-w-md mx-auto mb-4">
            <h3 className="font-semibold text-yellow-900 mb-2">
              ⚠️ Verification Status Reset
            </h3>
            <p className="text-sm text-yellow-800">
              Since you uploaded a new resume, your skills verification h
              reset. You'll need to re-verify your skills to access job tests.
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-xl p-6 max-w-md mx-auto">
            <h3 className="font-semibold text-green-900 mb-2">What's Next?</h3>
            <ul className="text-sm text-green-800 space-y-1 text-left">
              <li>• Your skills have been analyzed and categorized</li>
              <li>• Complete skills verification to access job tests</li>
              <li>• Relevant job matches will be suggested</li>
              <li>• Interview invitations may be sent</li>
            </ul>
          </div>

          <div className="mt-8 space-x-4">
            <Tooltip content="Upload a different resume file">
              <button
                onClick={handleReupload}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors relative"
              >
                Upload Different Resume
              </button>
            </Tooltip>
            <Tooltip content="View your updated profile">
              <button
                onClick={handleViewProfile}
                className="px-6 py-3 bg-red-800 text-white rounded-lg hover:bg-red-900 transition-colors relative"
              >
                View My Profile
              </button>
            </Tooltip>
          </div>
        </div>
      </div>
    );
  }

  if (uploadStep === "preview" && extractedData) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">
            Review Extracted Information
          </h1>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-blue-900 font-medium">
                  Please review the extracted information
                </p>
                <p className="text-blue-800 text-sm mt-1">
                  Make sure all details are correct before confirming. You can
                  edit any field if needed.
                </p>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Personal Information */}
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                Personal Information
              </h2>

              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <User className="w-5 h-5 text-gray-400" />
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={
                        extractedData.personalInfo?.name ||
                        extractedData.name ||
                        ""
                      }
                      onChange={(e) => {
                        setExtractedData({
                          ...extractedData,
                          personalInfo: {
                            ...extractedData.personalInfo,
                            name: e.target.value,
                          },
                        });
                      }}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-gray-400" />
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Email
                    </label>
                    <input
                      type="email"
                      value={
                        extractedData.personalInfo?.email ||
                        extractedData.email ||
                        ""
                      }
                      onChange={(e) => {
                        setExtractedData({
                          ...extractedData,
                          personalInfo: {
                            ...extractedData.personalInfo,
                            email: e.target.value,
                          },
                        });
                      }}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-gray-400" />
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Phone
                    </label>
                    <input
                      type="text"
                      value={
                        extractedData.personalInfo?.phone ||
                        extractedData.phone ||
                        ""
                      }
                      onChange={(e) => {
                        setExtractedData({
                          ...extractedData,
                          personalInfo: {
                            ...extractedData.personalInfo,
                            name: e.target.value,
                          },
                        });
                      }}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <MapPin className="w-5 h-5 text-gray-400" />
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Location
                    </label>
                    <input
                      type="text"
                      value={
                        extractedData.personalInfo?.location ||
                        extractedData.location ||
                        ""
                      }
                      onChange={(e) => {
                        setExtractedData({
                          ...extractedData,
                          personalInfo: {
                            ...extractedData.personalInfo,
                            name: e.target.value,
                          },
                        });
                      }}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Professional Information */}
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                Professional Information
              </h2>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Professional Summary
                </label>
                <textarea
                  value={
                    extractedData.professionalInfo?.summary ||
                    extractedData.summary ||
                    ""
                  }
                  onChange={(e) => {
                    setExtractedData({
                      ...extractedData,
                      professionalInfo: {
                        ...extractedData.professionalInfo,
                        summary: e.target.value,
                      },
                    });
                  }}
                  rows={4}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Experience Summary
                </label>
                <input
                  type="text"
                  value={(() => {
                    const exp = extractedData.professionalInfo?.experience || extractedData.experience;
                    // If experience is an array, convert to summary text
                    if (Array.isArray(exp)) {
                      return exp.map((e) => `${e.position || e.title || ''} at ${e.company || ''}`).join(', ') || 'See Work History below';
                    }
                    return exp || 'See Work History below';
                  })()}
                  readOnly
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Skills
                </label>
                <div className="flex flex-wrap gap-2 p-3 border border-gray-300 rounded-lg min-h-[100px]">
                  {(
                    extractedData.professionalInfo?.skills ||
                    extractedData.skills ||
                    []
                  ).map((skill, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-red-100 text-red-800 text-sm rounded-full"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>

              {/* Profile Classification */}
              <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg">
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  Profile Classification
                </label>

                {isClassifying ? (
                  <div className="flex items-center space-x-2 text-blue-600">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <span className="text-sm">Analyzing your profile...</span>
                  </div>
                ) : classification ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-700">Profile Type:</span>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${classification.isTechnical
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-green-100 text-green-800'
                          }`}>
                          {classification.isTechnical ? 'Technical' : 'Non-Technical'}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {classification.confidence}% confidence
                      </span>
                    </div>

                    <div>
                      <span className="text-sm text-gray-700">Primary Domain: </span>
                      <span className="text-sm font-medium text-gray-900">
                        {classification.primaryDomain}
                      </span>
                    </div>

                    {classification.reasoning && (
                      <p className="text-xs text-gray-600 italic">
                        {classification.reasoning}
                      </p>
                    )}

                    {/* Manual Override */}
                    <div className="mt-3 pt-3 border-t border-blue-200">
                      <label className="block text-xs font-medium text-gray-700 mb-2">
                        Want to change? Override classification:
                      </label>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setClassification({
                            ...classification,
                            isTechnical: true,
                            profileType: 'technical'
                          })}
                          className={`flex-1 px-3 py-2 text-sm rounded-lg border-2 transition-all ${classification.isTechnical
                            ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                            : 'border-gray-300 bg-white text-gray-700 hover:border-blue-300'
                            }`}
                        >
                          Technical
                        </button>
                        <button
                          type="button"
                          onClick={() => setClassification({
                            ...classification,
                            isTechnical: false,
                            profileType: 'non-technical'
                          })}
                          className={`flex-1 px-3 py-2 text-sm rounded-lg border-2 transition-all ${!classification.isTechnical
                            ? 'border-green-500 bg-green-50 text-green-700 font-medium'
                            : 'border-gray-300 bg-white text-gray-700 hover:border-green-300'
                            }`}
                        >
                          Non-Technical
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-600">
                    Classification will be determined automatically
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Work History */}
          <div className="mt-8">
            <h2 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2 mb-4">
              Work History
            </h2>
            <div className="space-y-4">
              {(extractedData.workHistory || []).map((job, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg">
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Job Title
                      </label>
                      <input
                        type="text"
                        value={job.title}
                        onChange={(e) => {
                          const newWorkHistory = [...extractedData.workHistory];
                          newWorkHistory[index] = {
                            ...job,
                            title: e.target.value,
                          };
                          setExtractedData({
                            ...extractedData,
                            workHistory: newWorkHistory,
                          });
                        }}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Company
                      </label>
                      <input
                        type="text"
                        value={job.company}
                        onChange={(e) => {
                          const newWorkHistory = [...extractedData.workHistory];
                          newWorkHistory[index] = {
                            ...job,
                            company: e.target.value,
                          };
                          setExtractedData({
                            ...extractedData,
                            workHistory: newWorkHistory,
                          });
                        }}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Duration
                      </label>
                      <input
                        type="text"
                        value={job.duration}
                        onChange={(e) => {
                          const newWorkHistory = [...extractedData.workHistory];
                          newWorkHistory[index] = {
                            ...job,
                            duration: e.target.value,
                          };
                          setExtractedData({
                            ...extractedData,
                            workHistory: newWorkHistory,
                          });
                        }}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500"
                      />
                    </div>
                  </div>
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Description
                    </label>
                    <textarea
                      value={job.description}
                      onChange={(e) => {
                        const newWorkHistory = [...extractedData.workHistory];
                        newWorkHistory[index] = {
                          ...job,
                          description: e.target.value,
                        };
                        setExtractedData({
                          ...extractedData,
                          workHistory: newWorkHistory,
                        });
                      }}
                      rows={2}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8 flex justify-between">
            <Tooltip content="Upload a different resume file">
              <button
                onClick={handleReupload}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors relative"
              >
                Upload Different File
              </button>
            </Tooltip>
            <Tooltip content="Confirm and save your profile data">
              <button
                onClick={handleConfirm}
                className="px-6 py-3 bg-red-800 text-white rounded-lg hover:bg-red-900 transition-colors relative"
              >
                Confirm & Save Profile
              </button>
            </Tooltip>
          </div>
        </div>
        {/* Loading Modal (shown during save + test generation) */}
        {isProcessing && savingStatus && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full mx-4">
              <div className="text-center">
                <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-6"></div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Processing Your Profile
                </h3>
                <p className="text-blue-600 font-medium mb-4">{savingStatus}</p>
                <div className="space-y-3 text-left">
                  <div className="flex items-center space-x-3">
                    {savingStatus.includes("Saving") ? (
                      <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    )}
                    <span className="text-sm text-gray-700">
                      Saving to database
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    {savingStatus.includes("Generating") ? (
                      <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <div className="w-5 h-5 border-2 border-gray-300 rounded-full"></div>
                    )}
                    <span className="text-sm text-gray-700">
                      Generating skill validation test
                    </span>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-6">
                  Please wait, this may take a few moments...
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Upload Your Resume
          </h1>
          <p className="text-lg text-gray-600">
            Upload your resume and let our Intelligence extract your professional
            information
          </p>
        </div>

        {isProcessing ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Processing Your Resume
            </h3>
            <p className="text-gray-600 mb-4">
              Our Intelligence is extracting information from your resume...
            </p>
            <div className="max-w-md mx-auto">
              <div className="bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full animate-pulse"
                  style={{ width: "70%" }}
                ></div>
              </div>
            </div>
          </div>
        ) : (
          <div
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            className={`border-2 border-dashed rounded-xl p-12 text-center transition-all ${isDragging
              ? "border-red-400 bg-red-50"
              : "border-gray-300 hover:border-red-400 hover:bg-red-50"
              }`}
          >
            <div className="space-y-6">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                <Upload className="w-10 h-10 text-red-600" />
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {isDragging
                    ? "Drop your resume here"
                    : "Drag & drop your resume"}
                </h3>
                <p className="text-gray-600 mb-4">or click to browse files</p>

                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="resume-upload"
                />
                <label
                  htmlFor="resume-upload"
                  className="inline-flex items-center px-6 py-3 bg-red-800 text-white rounded-lg hover:bg-red-900 transition-colors cursor-pointer"
                >
                  <FileText className="w-5 h-5 mr-2" />
                  Select Resume File
                </label>
              </div>

              <div className="text-sm text-gray-500">
                <p>Supported formats: PDF, DOC, DOCX</p>
                <p>Maximum file size: 10MB</p>
              </div>
            </div>
          </div>
        )}

        {file && !isProcessing && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-3">
              <FileText className="w-5 h-5 text-gray-600" />
              <div className="flex-1">
                <p className="font-medium text-gray-900">{file.name}</p>
                <p className="text-sm text-gray-600">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
          </div>
        )}
      </div>

      {/* Features */}
      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 text-center">
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
            <FileText className="w-6 h-6 text-blue-600" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-2">Smart Parsing</h3>
          <p className="text-sm text-gray-600">
            Intelligence extracts skills, experience, and contact information
            automatically
          </p>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 text-center">
          <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-6 h-6 text-green-600" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-2">Review & Edit</h3>
          <p className="text-sm text-gray-600">
            Review and edit extracted information before saving
          </p>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 text-center">
          <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mx-auto mb-4">
            <Upload className="w-6 h-6 text-red-600" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-2">Instant Matching</h3>
          <p className="text-sm text-gray-600">
            Get matched with relevant jobs and tests immediately
          </p>
        </div>
      </div>

      {/* Loading Modal */}
      {isProcessing && savingStatus && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full mx-4">
            <div className="text-center">
              {/* Animated Spinner */}
              <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-6"></div>

              {/* Status Message */}
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Processing Your Profile
              </h3>
              <p className="text-blue-600 font-medium mb-4">{savingStatus}</p>

              {/* Progress Indicators */}
              <div className="space-y-3 text-left">
                <div className="flex items-center space-x-3">
                  {savingStatus.includes("Saving") ? (
                    <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  )}
                  <span className="text-sm text-gray-700">
                    Saving to database
                  </span>
                </div>

                <div className="flex items-center space-x-3">
                  {savingStatus.includes("Generating") ? (
                    <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                  ) : savingStatus.includes("saved!") ? (
                    <div className="w-5 h-5 border-2 border-gray-300 rounded-full"></div>
                  ) : (
                    <div className="w-5 h-5 border-2 border-gray-300 rounded-full"></div>
                  )}
                  <span className="text-sm text-gray-700">
                    Generating skill validation test
                  </span>
                </div>
              </div>

              {/* Info Text */}
              <p className="text-xs text-gray-500 mt-6">
                Please wait, this may take a few moments...
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResumeUpload;
